import React, { useState, useEffect, useCallback } from 'react';
import UsersPage from './UsersPage';
import PracticeSetupWizard from '../components/PracticeSetupWizard';
import DataReadinessPage from './DataReadinessPage';
import ReportInboxAdmin from './ReportInboxAdmin';
import { SharedSmsNumberTab } from '../features/outreach';
import './AdminPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdminPage({ practices, onPracticesChanged, isSuperAdmin, onOpenPracticeData, canOnboard, onOpenPractice }) {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="admin-page">
      <h1 className="page-title">Admin</h1>
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`tab-button ${activeTab === 'practices' ? 'active' : ''}`}
          onClick={() => setActiveTab('practices')}
        >
          Practices
        </button>
        {/* Data readiness moved here from its own footer button (David, 2026-10-25).
            super_admin only, like the endpoint behind it. */}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'data-readiness' ? 'active' : ''}`}
            onClick={() => setActiveTab('data-readiness')}
          >
            Data readiness
          </button>
        )}
        {/* Email ingest: the emailed-report inbox's routing table (docs/report-inbox.md).
            super_admin only, like the endpoints behind it. */}
        {isSuperAdmin && (
          <button
            className={`tab-button ${activeTab === 'email-ingest' ? 'active' : ''}`}
            onClick={() => setActiveTab('email-ingest')}
          >
            Email ingest
          </button>
        )}
        {/* Outreach: the one Twilio number Atlas texts patients from (Jordan,
            2026-10-26). Admin, like this page; the endpoints check the role again. */}
        <button
          className={`tab-button ${activeTab === 'outreach' ? 'active' : ''}`}
          onClick={() => setActiveTab('outreach')}
        >
          Outreach
        </button>
        {/* Payments tab hidden on the Admin screen per David (2026-07-27). This
            button was the only entry point to PaymentsSection, so that section
            is now unreachable rather than deleted. Restore by reinstating:
              <button
                className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
                onClick={() => setActiveTab('payments')}
              >
                Payments
              </button>
            Unrelated to the client-facing Payments page in the sidebar
            (PaymentsPage.js), which is untouched. */}
      </div>

      <div className="detail-content">
        {activeTab === 'users' && <UsersPage practices={practices} />}
        {activeTab === 'practices' && (
          <PracticesSection onPracticesChanged={onPracticesChanged} canOnboard={canOnboard} onOpenPractice={onOpenPractice} />
        )}
        {activeTab === 'payments' && <PaymentsSection />}
        {isSuperAdmin && activeTab === 'data-readiness' && (
          <DataReadinessPage onOpenPracticeData={onOpenPracticeData} embedded />
        )}
        {isSuperAdmin && activeTab === 'email-ingest' && <ReportInboxAdmin practices={practices} />}
        {activeTab === 'outreach' && <SharedSmsNumberTab />}
      </div>
    </div>
  );
}

const PRACTICE_SORT_COLUMNS = [
  { key: 'name', label: 'Practice' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Created', type: 'number' },
  { key: 'emr_system', label: 'EMR' },
  { key: 'clearinghouse', label: 'Clearinghouse' },
  { key: 'mtd_collections', label: 'MTD Collections', type: 'number', figure: true },
  { key: 'elig_non_medicare', label: 'Elig. Non-Medicare', type: 'number', figure: true },
  { key: 'elig_medicare', label: 'Elig. Medicare', type: 'number', figure: true },
];

// Today in the browser's timezone, so the month does not roll over on the server's UTC clock.
function localToday() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function formatFigure(key, value) {
  if (value === null || value === undefined) return '-';
  if (key === 'mtd_collections') {
    return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `${Number(value).toFixed(1)}%`;
}

// "answered of patients" on hover for the two eligibility rates.
function figureTitle(key, f) {
  const n = f && f[`${key}_n`];
  if (!n) return undefined;
  const runs = f.elig_runs;
  return `${n[0].toLocaleString()} of ${n[1].toLocaleString()} patients, last ${runs} email${runs === 1 ? '' : 's'}`;
}

const PRACTICE_STATUS_OPTIONS = ['Active', 'Inactive'];

/**
 * Mark practices active / inactive. Inactive practices are hidden from the practice
 * picker and skipped by the daily sync, so this tab is the only place they appear.
 * Laid out like the Users tab and borrows its styles (hence `users-section`).
 */
function PracticesSection({ onPracticesChanged, canOnboard, onOpenPractice }) {
  const [showCreate, setShowCreate] = useState(false);
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  // Active by default: inactive practices are the exception, not the working set.
  const [statusFilter, setStatusFilter] = useState('Active');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [openMenuId, setOpenMenuId] = useState(null);
  // Fetched apart from the list: the collections read is slow, and the table should not wait on it.
  const [figures, setFigures] = useState({});

  // Same outside-click / Escape handling as the Users tab's row menu.
  useEffect(() => {
    if (openMenuId === null) return undefined;
    const close = () => setOpenMenuId(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const loadPractices = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/practices/all`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setPractices(data.practices || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to load practices' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load practices' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPractices(); }, [loadPractices]);

  useEffect(() => {
    fetch(`${API_URL}/api/practices/figures?report_date=${localToday()}`, { credentials: 'include' })
      .then(resp => (resp.ok ? resp.json() : null))
      .then(data => { if (data) setFigures(data.figures || {}); })
      .catch(() => {});
  }, []);

  const handleToggleEnabled = async (practice) => {
    const enabled = !practice.enabled;
    const detail = enabled
      ? 'It will reappear in the practice picker and resume daily syncing.'
      : 'It will be hidden from the practice picker and skipped by the daily sync. No data is deleted.';
    if (!window.confirm(`${enabled ? 'Reactivate' : 'Deactivate'} ${practice.name}?\n\n${detail}`)) return;

    try {
      const resp = await fetch(`${API_URL}/api/practices/${practice.id}/enabled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled }),
      });
      if (!resp.ok) {
        const data = await resp.json();
        setMessage({ type: 'error', text: data.error || 'Failed' });
        return;
      }
      setMessage({ type: 'success', text: `${practice.name} ${enabled ? 'reactivated' : 'deactivated'}` });
      loadPractices();
      // Refresh the sidebar picker so the change shows there without a reload.
      if (onPracticesChanged) {
        onPracticesChanged().catch(() => {
          setMessage({ type: 'error', text: 'Saved, but the practice picker failed to refresh. Reload the page.' });
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
  };

  const statusOf = (p) => (p.enabled ? 'Active' : 'Inactive');

  // created_at is naive UTC from the server; tag it so the browser converts to local.
  const createdMs = (p) => (p.created_at ? Date.parse(`${p.created_at}Z`) : null);

  const sortValue = (p, key) => {
    if (key === 'status') return statusOf(p);
    if (key === 'created_at') return createdMs(p);
    if (PRACTICE_SORT_COLUMNS.find(c => c.key === key)?.figure) {
      const v = figures[p.id] && figures[p.id][key];
      return v === null || v === undefined ? null : v;
    }
    return p[key] || '';
  };

  const toggleSort = (key) => {
    setSort(prev => (
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    ));
  };

  const numericSort = PRACTICE_SORT_COLUMNS.find(c => c.key === sort.key)?.type === 'number';

  const visiblePractices = practices
    .filter(p => statusFilter === 'all' || statusOf(p) === statusFilter)
    .sort((a, b) => {
      if (numericSort) {
        // Unknown dates sink to the bottom in both directions, as on the Users tab.
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(sortValue(a, sort.key)).localeCompare(
        String(sortValue(b, sort.key)), undefined, { sensitivity: 'base' }
      );
      return sort.dir === 'asc' ? cmp : -cmp;
    });

  return (
    <div className="users-section">
      <div className="tab-header">
        <div className="claims-filter-bar">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            {PRACTICE_STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
            <option value="all">All statuses</option>
          </select>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          Add New Practice
        </button>
      </div>

      {showCreate && (
        <PracticeSetupWizard
          canOnboard={canOnboard}
          onPracticesUpdated={onPracticesChanged}
          onClose={(practice) => {
            setShowCreate(false);
            if (!practice) return;
            loadPractices();
            if (onOpenPractice) onOpenPractice(practice);
          }}
        />
      )}

      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <span className="message-content">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner"></div>Loading...</div>
      ) : practices.length === 0 ? (
        <div className="empty-message">No practices found</div>
      ) : visiblePractices.length === 0 ? (
        <div className="empty-message">
          {statusFilter === 'Inactive' ? 'No inactive practices' : 'No practices match this filter'}
        </div>
      ) : (
        <table className="detail-table">
          <thead>
            <tr>
              {PRACTICE_SORT_COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="sortable"
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}
                  <span className="sort-arrow">
                    {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
              ))}
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visiblePractices.map(p => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  <span className={`user-status-badge ${p.enabled ? 'active' : 'inactive'}`}>
                    {statusOf(p)}
                  </span>
                </td>
                <td>
                  {createdMs(p) !== null ? new Date(createdMs(p)).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                </td>
                <td>{p.emr_system || '-'}</td>
                <td>{p.clearinghouse || '-'}</td>
                {PRACTICE_SORT_COLUMNS.filter(c => c.figure).map(c => (
                  <td key={c.key} className="col-figure" title={figureTitle(c.key, figures[p.id])}>
                    {formatFigure(c.key, figures[p.id] && figures[p.id][c.key])}
                  </td>
                ))}
                <td className="col-actions">
                  <div className="user-actions">
                    <button
                      className="btn-link user-menu-trigger"
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === p.id}
                      aria-label={`Actions for ${p.name}`}
                      onMouseDown={e => e.stopPropagation()}
                      onClick={() => setOpenMenuId(openMenuId === p.id ? null : p.id)}
                    >
                      &#8943;
                    </button>
                    {openMenuId === p.id && (
                      // stopPropagation for the same reason as the Users tab menu:
                      // the closing listener fires on mousedown, before click lands.
                      <div
                        className="user-menu"
                        role="menu"
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <button
                          role="menuitem"
                          className={`user-menu-item ${p.enabled ? 'warning' : 'success'}`}
                          onClick={() => { setOpenMenuId(null); handleToggleEnabled(p); }}
                        >
                          {p.enabled ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PaymentsSection() {
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Invoices from Stripe
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Single-practice month billing
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [billingMonth, setBillingMonth] = useState(defaultMonth);
  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);

  // Editable bill fields
  const [edits, setEdits] = useState({ manual_collections: 0, credentialing_hours: 0, credentialing_rate: 25 });
  const [savingBill, setSavingBill] = useState(false);
  const [invoicingId, setInvoicingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  // Inline contract editing
  const [configEditing, setConfigEditing] = useState(false);
  const [configForm, setConfigForm] = useState({ collections_fee_pct: '', monthly_minimum: '', stripe_customer_id: '' });
  const [savingConfig, setSavingConfig] = useState(false);

  const selectedPractice = practices.find(p => p.id === selectedPracticeId);

  const loadPractices = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const list = data.practices || [];
        setPractices(list);
        if (list.length > 0 && !selectedPracticeId) {
          setSelectedPracticeId(list[0].id);
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to load billing practices' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to load billing practices' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    if (!selectedPracticeId) return;
    setInvoicesLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/invoices`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setInvoices(data.invoices || []);
      } else {
        setInvoices([]);
      }
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [selectedPracticeId]);

  const loadMonthData = useCallback(async () => {
    if (!selectedPracticeId) return;
    setMonthLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/month/${billingMonth}`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setMonthData(data);
        const b = data.billing;
        setEdits({
          manual_collections: b ? b.manual_collections : 0,
          credentialing_hours: b ? b.credentialing_hours : 0,
          credentialing_rate: b ? b.credentialing_rate : 25,
        });
      } else {
        setMonthData(null);
      }
    } catch {
      setMonthData(null);
    } finally {
      setMonthLoading(false);
    }
  }, [selectedPracticeId, billingMonth]);

  useEffect(() => { loadPractices(); }, [loadPractices]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { loadMonthData(); }, [loadMonthData]);

  // Reset config editing when practice changes
  useEffect(() => { setConfigEditing(false); }, [selectedPracticeId]);

  const changeMonth = (delta) => {
    const [y, m] = billingMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setBillingMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const startConfigEdit = () => {
    if (!selectedPractice) return;
    setConfigForm({
      collections_fee_pct: selectedPractice.collections_fee_pct ?? '',
      monthly_minimum: selectedPractice.monthly_minimum ?? '',
      stripe_customer_id: selectedPractice.stripe_customer_id || '',
    });
    setConfigEditing(true);
  };

  const saveConfig = async () => {
    if (!selectedPracticeId) return;
    setSavingConfig(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/practices/${selectedPracticeId}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          collections_fee_pct: configForm.collections_fee_pct !== '' ? parseFloat(configForm.collections_fee_pct) : null,
          monthly_minimum: configForm.monthly_minimum !== '' ? parseFloat(configForm.monthly_minimum) : null,
          stripe_customer_id: configForm.stripe_customer_id || null,
        }),
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Billing config updated' });
        setConfigEditing(false);
        loadPractices();
        loadInvoices();
        loadMonthData();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to save config' });
    } finally {
      setSavingConfig(false);
    }
  };

  const saveBilling = async () => {
    if (!selectedPracticeId) return;
    setSavingBill(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/month/${billingMonth}/practice/${selectedPracticeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          manual_collections: parseFloat(edits.manual_collections) || 0,
          credentialing_hours: parseFloat(edits.credentialing_hours) || 0,
          credentialing_rate: parseFloat(edits.credentialing_rate) || 25,
        }),
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Billing saved' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to save billing' });
    } finally {
      setSavingBill(false);
    }
  };

  const sendInvoice = async (billingId) => {
    setInvoicingId(billingId);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/${billingId}/send-invoice`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Invoice sent' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to send invoice' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to send invoice' });
    } finally {
      setInvoicingId(null);
    }
  };

  const syncStatus = async (billingId) => {
    setSyncingId(billingId);
    try {
      const resp = await fetch(`${API_URL}/api/admin/billing/${billingId}/sync-status`, {
        method: 'POST',
        credentials: 'include',
      });
      if (resp.ok) {
        setMessage({ type: 'success', text: 'Invoice status synced' });
        loadMonthData();
        loadInvoices();
      } else {
        const err = await resp.json();
        setMessage({ type: 'error', text: err.error || 'Failed to sync' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to sync status' });
    } finally {
      setSyncingId(null);
    }
  };

  const fmt = (n) => {
    if (n == null) return '-';
    return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };

  if (loading) return <div className="loading-state"><div className="spinner"></div>Loading...</div>;

  // Compute bill line items from monthData
  const b = monthData?.billing;
  const eraAmt = b ? b.era_collections : (monthData?.era_collections || 0);
  const manualAmt = parseFloat(edits.manual_collections) || 0;
  const totalCol = eraAmt + manualAmt;
  const feePct = selectedPractice?.collections_fee_pct;
  const minimum = selectedPractice?.monthly_minimum || 0;
  const pctFee = feePct != null ? totalCol * feePct / 100 : 0;
  const collFee = Math.max(pctFee, minimum);
  const credHrs = parseFloat(edits.credentialing_hours) || 0;
  const credRate = parseFloat(edits.credentialing_rate) || 25;
  const credFee = credHrs * credRate;
  const totalFee = collFee + credFee;
  const invoiceStatus = b ? b.invoice_status : 'draft';
  const isLocked = b && b.invoice_status !== 'draft';

  return (
    <div className="payments-section">
      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <span className="message-content">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {/* Practice Dropdown */}
      <div className="payments-practice-selector">
        <select
          value={selectedPracticeId || ''}
          onChange={(e) => setSelectedPracticeId(parseInt(e.target.value, 10))}
        >
          {practices.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {selectedPractice && (
        <>
          {/* Top Row: Contract Details + Invoices */}
          <div className="payments-top-row">
            {/* Left: Contract Details */}
            <div className="payments-panel">
              <div className="payments-panel-header">
                <h4>Contract Details</h4>
                {!configEditing && (
                  <button className="btn btn-secondary btn-sm" onClick={startConfigEdit}>Edit</button>
                )}
              </div>
              <div className="payments-panel-body">
                {configEditing ? (
                  <>
                    <table className="contract-details-table">
                      <tbody>
                        <tr>
                          <td>Collections Rate</td>
                          <td>
                            <input
                              type="number" min="0" max="100" step="0.01"
                              className="inline-input"
                              value={configForm.collections_fee_pct}
                              onChange={(e) => setConfigForm(f => ({ ...f, collections_fee_pct: e.target.value }))}
                              placeholder="e.g. 7.5"
                            /> %
                          </td>
                        </tr>
                        <tr>
                          <td>Monthly Minimum</td>
                          <td>
                            $ <input
                              type="number" min="0" step="0.01"
                              className="inline-input"
                              value={configForm.monthly_minimum}
                              onChange={(e) => setConfigForm(f => ({ ...f, monthly_minimum: e.target.value }))}
                              placeholder="e.g. 500"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Stripe Customer</td>
                          <td>
                            <input
                              type="text"
                              className="inline-input"
                              style={{ width: '100%' }}
                              value={configForm.stripe_customer_id}
                              onChange={(e) => setConfigForm(f => ({ ...f, stripe_customer_id: e.target.value }))}
                              placeholder="cus_..."
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="contract-edit-actions">
                      <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={savingConfig}>
                        {savingConfig ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setConfigEditing(false)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <table className="contract-details-table">
                    <tbody>
                      <tr>
                        <td>Collections Rate</td>
                        <td>{selectedPractice.collections_fee_pct != null ? `${selectedPractice.collections_fee_pct}%` : <span className="text-muted">Not set</span>}</td>
                      </tr>
                      <tr>
                        <td>Monthly Minimum</td>
                        <td>{selectedPractice.monthly_minimum != null ? fmt(selectedPractice.monthly_minimum) : <span className="text-muted">Not set</span>}</td>
                      </tr>
                      <tr>
                        <td>Stripe Customer</td>
                        <td>{selectedPractice.stripe_customer_id ? <span title={selectedPractice.stripe_customer_id}>{selectedPractice.stripe_customer_id}</span> : <span className="text-muted">Not configured</span>}</td>
                      </tr>
                      <tr>
                        <td>Clearinghouse</td>
                        <td>{selectedPractice.clearinghouse || <span className="text-muted">None</span>}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Right: Invoices */}
            <div className="payments-panel">
              <div className="payments-panel-header">
                <h4>Payments / Invoices</h4>
              </div>
              {!selectedPractice.stripe_customer_id ? (
                <div className="invoices-empty">No Stripe customer configured</div>
              ) : invoicesLoading ? (
                <div className="invoices-empty"><div className="spinner"></div> Loading invoices...</div>
              ) : invoices.length === 0 ? (
                <div className="invoices-empty">No invoices found</div>
              ) : (
                <div className="payments-invoices-scroll">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id}>
                          <td>{new Date(inv.created).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                          <td className="col-currency">{fmt(inv.amount_due)}</td>
                          <td><span className={`invoice-badge ${inv.status}`}>{inv.status}</span></td>
                          <td>
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="btn-link" style={{ fontSize: 12 }}>
                                View
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* New Bill */}
          <div className="payments-new-bill">
            <div className="payments-new-bill-header">
              <h4>New Bill &mdash; {formatMonth(billingMonth)}</h4>
              <div className="month-selector">
                <button className="btn btn-secondary btn-sm" onClick={() => changeMonth(-1)}>&larr;</button>
                <input
                  type="month"
                  value={billingMonth}
                  onChange={(e) => setBillingMonth(e.target.value)}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => changeMonth(1)}>&rarr;</button>
              </div>
            </div>
            {monthLoading ? (
              <div className="payments-new-bill-body">
                <div className="loading-state"><div className="spinner"></div>Loading...</div>
              </div>
            ) : (
              <div className="payments-new-bill-body">
                <div className="bill-summary">
                  {/* Left column: Collections */}
                  <div>
                    <div className="bill-line">
                      <span className="bill-line-label">Automated ERA Collections</span>
                      <span className="bill-line-value">{fmt(eraAmt)}</span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">+ Additional</span>
                      <span className="bill-line-input">
                        <input
                          type="number" className="inline-input" min="0" step="0.01"
                          value={edits.manual_collections}
                          onChange={(e) => setEdits(prev => ({ ...prev, manual_collections: e.target.value }))}
                          disabled={isLocked}
                        />
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">= Total Collections</span>
                      <span className="bill-line-value"><strong>{fmt(totalCol)}</strong></span>
                    </div>
                  </div>

                  {/* Right column: Fees */}
                  <div>
                    <div className="bill-line">
                      <span className="bill-line-label">Collections Fee</span>
                      <span className="bill-line-value">
                        {fmt(b ? b.collections_fee : collFee)}
                        {feePct != null && (
                          <span className="fee-breakdown" style={{ marginLeft: 8 }}>
                            {feePct}% x {fmt(totalCol)}{minimum > 0 ? `, min ${fmt(minimum)}` : ''}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">Cred Hours</span>
                      <span className="bill-line-input">
                        <input
                          type="number" className="inline-input inline-input-sm" min="0" step="0.25"
                          value={edits.credentialing_hours}
                          onChange={(e) => setEdits(prev => ({ ...prev, credentialing_hours: e.target.value }))}
                          disabled={isLocked}
                        />
                        <span className="fee-breakdown">x ${credRate}/hr = {fmt(b ? b.credentialing_fee : credFee)}</span>
                      </span>
                    </div>
                    <div className="bill-line">
                      <span className="bill-line-label">Total Fee</span>
                      <span className="bill-line-value total"><strong>{fmt(b ? b.total_fee : totalFee)}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="bill-actions">
                  <span className={`invoice-badge ${invoiceStatus}`} style={{ alignSelf: 'center' }}>
                    {invoiceStatus}
                  </span>
                  {!isLocked && (
                    <button className="btn btn-primary btn-sm" onClick={saveBilling} disabled={savingBill}>
                      {savingBill ? 'Saving...' : 'Save Draft'}
                    </button>
                  )}
                  {b && b.id && invoiceStatus === 'draft' && selectedPractice.stripe_customer_id && !b.stripe_invoice_id && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => sendInvoice(b.id)}
                      disabled={invoicingId === b.id}
                    >
                      {invoicingId === b.id ? 'Creating...' : 'Create Stripe Draft'}
                    </button>
                  )}
                  {b && b.stripe_invoice_id && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => syncStatus(b.id)}
                      disabled={syncingId === b.id}
                    >
                      {syncingId === b.id ? 'Syncing...' : 'Sync Status'}
                    </button>
                  )}
                  {b && b.stripe_invoice_url && (
                    <a href={b.stripe_invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
                      View Invoice
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPage;
